import { useState, useEffect, useRef, useCallback } from 'react';
import { Bug, Lightbulb, X, ImagePlus, Send, Check } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content;
const appVersion = () => document.querySelector('meta[name="app-version"]')?.content || '';

const MAX_PRILOHY = 5;
const MAX_VELIKOST = 5 * 1024 * 1024; // 5 MB

/**
 * Plovoucí tlačítko pro rychlé nahlášení podnětu (chyba / nápad).
 * Viditelné na každé stránce. Automaticky zachytí technický kontext.
 */
export default function PodnetWidget({ currentPage, user, isMobile, showFab = true }) {
    const { theme } = useTheme();
    const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

    const [open, setOpen]       = useState(false);
    const [typ, setTyp]         = useState('chyba');
    const [titulek, setTitulek] = useState('');
    const [popis, setPopis]     = useState('');
    const [prilohy, setPrilohy] = useState([]); // { file, url }
    const [odesilam, setOdesilam] = useState(false);
    const [hotovo, setHotovo]   = useState(false);
    const [chyba, setChyba]     = useState('');
    const fileRef = useRef(null);

    // Otevření z jiných míst aplikace (tlačítko "Nový podnět" na stránce Podněty)
    useEffect(() => {
        const onOpen = () => setOpen(true);
        window.addEventListener('open-podnet', onOpen);
        return () => window.removeEventListener('open-podnet', onOpen);
    }, []);

    const reset = () => {
        setTyp('chyba'); setTitulek(''); setPopis('');
        prilohy.forEach(p => URL.revokeObjectURL(p.url));
        setPrilohy([]); setChyba(''); setHotovo(false);
    };

    const zavri = () => { setOpen(false); reset(); };

    const pridejSoubory = useCallback((files) => {
        setChyba('');
        const nove = [];
        for (const file of files) {
            if (prilohy.length + nove.length >= MAX_PRILOHY) { setChyba(`Maximálně ${MAX_PRILOHY} příloh.`); break; }
            if (file.size > MAX_VELIKOST) { setChyba(`Soubor „${file.name}" je větší než 5 MB.`); continue; }
            if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
                setChyba('Povolené jsou jen obrázky a PDF.'); continue;
            }
            nove.push({ file, url: URL.createObjectURL(file) });
        }
        if (nove.length) setPrilohy(prev => [...prev, ...nove]);
    }, [prilohy.length]);

    // Vložení screenshotu ze schránky (Ctrl+V)
    useEffect(() => {
        if (!open) return;
        const onPaste = (e) => {
            const obrazky = [...(e.clipboardData?.items || [])]
                .filter(i => i.type.startsWith('image/'))
                .map(i => i.getAsFile())
                .filter(Boolean);
            if (obrazky.length) { e.preventDefault(); pridejSoubory(obrazky); }
        };
        window.addEventListener('paste', onPaste);
        return () => window.removeEventListener('paste', onPaste);
    }, [open, pridejSoubory]);

    const odebrPrilohu = (idx) => {
        setPrilohy(prev => {
            URL.revokeObjectURL(prev[idx].url);
            return prev.filter((_, i) => i !== idx);
        });
    };

    const odeslat = async () => {
        if (!titulek.trim() || !popis.trim()) { setChyba('Vyplň název i popis.'); return; }
        setOdesilam(true); setChyba('');

        const fd = new FormData();
        fd.append('typ', typ);
        fd.append('titulek', titulek.trim());
        fd.append('popis', popis.trim());
        fd.append('kontext_stranka', currentPage || '');
        fd.append('kontext_url', window.location.href);
        fd.append('kontext_verze', appVersion());
        fd.append('kontext_prohlizec', navigator.userAgent);
        fd.append('kontext_zarizeni', `${window.innerWidth < 768 ? 'Mobil' : 'Desktop'} ${window.innerWidth}×${window.innerHeight}`);
        prilohy.forEach(p => fd.append('prilohy[]', p.file));

        try {
            const res = await fetch('/api/podnety', {
                method: 'POST', headers: { 'X-CSRF-TOKEN': csrf() }, body: fd,
            });
            if (!res.ok) throw new Error();
            setHotovo(true);
            window.dispatchEvent(new Event('podnet-created'));
            setTimeout(zavri, 1400);
        } catch {
            setChyba('Nepodařilo se odeslat. Zkus to prosím znovu.');
        } finally {
            setOdesilam(false);
        }
    };

    // ── Plovoucí tlačítko ──
    const fabBottom = isMobile
        ? 'calc(62px + env(safe-area-inset-bottom, 0px))'
        : '24px';

    return (
        <>
            {/* Plovoucí FAB jen na desktop — na mobilu se podnět nehlásí z globální navigace. */}
            {showFab && !open && !isMobile && (
                <button
                    onClick={() => setOpen(true)}
                    title="Nahlásit chybu nebo nápad"
                    style={{
                        position: 'fixed', bottom: fabBottom, right: '24px',
                        width: 48, height: 48, borderRadius: '50%', zIndex: 150,
                        background: '#B47AEA', color: '#fff', border: 'none', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 4px 16px rgba(180,122,234,0.45)',
                    }}
                >
                    <Bug size={22} />
                </button>
            )}

            {open && (
                <div
                    onClick={zavri}
                    style={{
                        position: 'fixed', inset: 0, zIndex: 250,
                        background: 'rgba(0,0,0,0.55)',
                        display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center',
                        padding: isMobile ? '0' : '16px',
                    }}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{
                            width: '100%', maxWidth: 460,
                            maxHeight: isMobile ? '92vh' : '90vh', overflowY: 'auto',
                            background: isDark ? 'rgba(28,28,38,0.99)' : '#fff',
                            border: '1px solid var(--border-default)',
                            borderRadius: isMobile ? '16px 16px 0 0' : 16,
                        }}
                    >
                        {/* Hlavička */}
                        <div className="flex items-center justify-between"
                            style={{ padding: '16px 18px', borderBottom: '1px solid var(--border-default)' }}>
                            <h3 className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>Nahlásit podnět</h3>
                            <button onClick={zavri} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                                <X size={20} />
                            </button>
                        </div>

                        {hotovo ? (
                            <div className="text-center" style={{ padding: '48px 24px' }}>
                                <div className="flex items-center justify-center mx-auto mb-3"
                                    style={{ width: 52, height: 52, borderRadius: '50%', background: 'rgba(16,185,129,0.15)' }}>
                                    <Check size={28} style={{ color: '#10B981' }} />
                                </div>
                                <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Děkujeme!</p>
                                <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>Podnět byl odeslán.</p>
                            </div>
                        ) : (
                            <div style={{ padding: 18 }}>
                                {/* Typ */}
                                <div className="flex gap-2 mb-3">
                                    <TypButton active={typ === 'chyba'} onClick={() => setTyp('chyba')}
                                        Icon={Bug} label="Chyba" color="#EF4444" isDark={isDark} />
                                    <TypButton active={typ === 'napad'} onClick={() => setTyp('napad')}
                                        Icon={Lightbulb} label="Nápad" color="#F59E0B" isDark={isDark} />
                                </div>

                                {/* Titulek */}
                                <input
                                    value={titulek}
                                    onChange={e => setTitulek(e.target.value)}
                                    maxLength={160}
                                    placeholder={typ === 'chyba' ? 'Co nefunguje?' : 'Co navrhuješ?'}
                                    className="w-full text-sm px-3 py-2 rounded-lg mb-2.5 outline-none"
                                    style={{ background: 'var(--surface-elevated)', color: 'var(--text-primary)', border: '1px solid var(--border-default)' }}
                                />

                                {/* Popis */}
                                <textarea
                                    value={popis}
                                    onChange={e => setPopis(e.target.value)}
                                    rows={4}
                                    placeholder={typ === 'chyba'
                                        ? 'Popiš co se stalo a jak chybu vyvolat…'
                                        : 'Popiš svůj nápad podrobněji…'}
                                    className="w-full text-sm px-3 py-2 rounded-lg mb-2.5 outline-none resize-none"
                                    style={{ background: 'var(--surface-elevated)', color: 'var(--text-primary)', border: '1px solid var(--border-default)' }}
                                />

                                {/* Přílohy */}
                                <div className="flex flex-wrap gap-2 mb-2.5">
                                    {prilohy.map((p, i) => (
                                        <div key={i} style={{ position: 'relative' }}>
                                            {p.file.type.startsWith('image/') ? (
                                                <img src={p.url} alt="" style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--border-default)' }} />
                                            ) : (
                                                <div className="flex items-center justify-center text-[10px] text-center"
                                                    style={{ width: 60, height: 60, borderRadius: 6, background: 'var(--surface-elevated)', border: '1px solid var(--border-default)', color: 'var(--text-muted)' }}>
                                                    PDF
                                                </div>
                                            )}
                                            <button onClick={() => odebrPrilohu(i)}
                                                style={{
                                                    position: 'absolute', top: -6, right: -6, width: 18, height: 18,
                                                    borderRadius: '50%', background: '#EF4444', color: '#fff',
                                                    border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                }}>
                                                <X size={11} />
                                            </button>
                                        </div>
                                    ))}
                                    {prilohy.length < MAX_PRILOHY && (
                                        <button onClick={() => fileRef.current?.click()}
                                            className="flex flex-col items-center justify-center gap-0.5 text-[10px]"
                                            style={{
                                                width: 60, height: 60, borderRadius: 6, cursor: 'pointer',
                                                background: 'var(--surface-elevated)', color: 'var(--text-muted)',
                                                border: '1px dashed var(--border-default)',
                                            }}>
                                            <ImagePlus size={16} />
                                        </button>
                                    )}
                                </div>
                                <input
                                    ref={fileRef} type="file" accept="image/*,application/pdf" multiple hidden
                                    onChange={e => { pridejSoubory([...e.target.files]); e.target.value = ''; }}
                                />
                                <p className="text-[11px] mb-3" style={{ color: 'var(--text-muted)' }}>
                                    Screenshot lze vložit i ze schránky (Ctrl+V). Max 5 MB / soubor.
                                </p>

                                {chyba && (
                                    <p className="text-xs mb-2.5" style={{ color: '#EF4444' }}>{chyba}</p>
                                )}

                                {/* Odeslat */}
                                <button
                                    onClick={odeslat}
                                    disabled={odesilam}
                                    className="w-full flex items-center justify-center gap-1.5 text-sm font-semibold py-2.5 rounded-lg"
                                    style={{ background: '#B47AEA', color: '#fff', border: 'none', cursor: 'pointer', opacity: odesilam ? 0.6 : 1 }}
                                >
                                    <Send size={15} /> {odesilam ? 'Odesílám…' : 'Odeslat podnět'}
                                </button>
                                <p className="text-[10px] text-center mt-2" style={{ color: 'var(--text-muted)' }}>
                                    K podnětu se automaticky připojí stránka, verze aplikace a prohlížeč.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}

function TypButton({ active, onClick, Icon, label, color, isDark }) {
    return (
        <button
            onClick={onClick}
            className="flex-1 flex items-center justify-center gap-1.5 text-sm font-medium py-2 rounded-lg"
            style={{
                background: active ? color + '20' : (isDark ? 'rgba(255,255,255,0.04)' : 'var(--surface-elevated)'),
                color: active ? color : 'var(--text-secondary)',
                border: `1px solid ${active ? color + '55' : 'var(--border-default)'}`,
                cursor: 'pointer', transition: 'all 0.15s ease',
            }}
        >
            <Icon size={15} /> {label}
        </button>
    );
}
